import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { useStore } from '../../../store/store';
import useSubscriptionStatus from '../../../hooks/useSubscriptionStatus';
import useAgentUsageRefetch from './useAgentUsageRefetch';
import AgentTokenDisplay from './AgentTokenDisplay';
import AgentUsageMeter from './AgentUsageMeter';

const CREDITS_INFO = 'Used when contact details are revealed.';
const DOWNLOADS_INFO = 'Used when a company profile is exported.';

// Skeleton reserves a meter's footprint so the sidebar doesn't pop the block in
// from nothing when the figures land. The panel can still grow if the contract
// carries more than one balance — the meter count is only known once the data
// arrives, so a fully fixed height isn't available to us here.
const renderSkeleton = () => (
  <div className="agent-usage-panel">
    <div className="agent-usage-meter agent-usage-meter-skeleton" aria-hidden="true">
      <span className="company-tokens-label">&nbsp;</span>
      <div className="usage-progress-track" />
      <span className="agent-usage-meter-value">&nbsp;</span>
    </div>
  </div>
);

/**
 * The usage block at the foot of the agent sidebar (TD-2883).
 *
 * Owns the single /subscription-status instance for the whole block and renders
 * the balances the contract actually carries, always in the same order:
 * Company Tokens, Contact credits, Company downloads. Each meter is independent
 * — one balance can be spent while the others are healthy, and a balance that
 * is not part of the contract is absent rather than shown as a zero.
 *
 * Two gates are deliberately NOT `subscribed`:
 *   - Contact credits: a demo enrichment user comes back as `subscribed: false`
 *     WITH an enrichment block (TD-2333), so gating on `subscribed` would hide a
 *     balance they can actually spend. The Cognito group gate still applies, as
 *     it does everywhere else the credit balance is shown.
 *   - `configured` (not `limit`) decides whether downloads and credits render:
 *     the BE sends `null` for "no allowance on this contract" and `0` for "an
 *     allowance of zero", and the hook's `limit ?? 0` normalisation would
 *     otherwise make a demo contract look unconfigured.
 *
 * The credit meter is intentionally not behind AGENT_ENRICHMENT_ENABLED: that
 * flag gates the agent's enrichment ACTIONS, not the display of a balance the
 * profiler already shows unflagged.
 */
const AgentUsagePanel = observer(({ collapsed = false }) => {
  const { profilerAccessStore } = useStore();
  const { data, subscribed, tokens, enrichment, downloads, isLoading, refetch, ackNotification } =
    useSubscriptionStatus();

  useAgentUsageRefetch(refetch);

  const tokensVisible = subscribed && !!tokens?.limit;
  const creditsVisible = profilerAccessStore.isCognitoContactEnrichment && !!enrichment?.configured;
  const downloadsVisible = subscribed && !!downloads?.configured;

  if (isLoading && !data) return renderSkeleton();
  if (!tokensVisible && !creditsVisible && !downloadsVisible) return null;

  return (
    <div className={`agent-usage-panel${collapsed ? ' agent-usage-panel-compact' : ''}`}>
      {tokensVisible && (
        <AgentTokenDisplay
          collapsed={collapsed}
          tokens={tokens}
          subscribed={subscribed}
          isLoading={isLoading && !data}
          ackNotification={ackNotification}
        />
      )}
      {creditsVisible && (
        <AgentUsageMeter
          label="Contact credits"
          infoText={CREDITS_INFO}
          used={enrichment.used}
          limit={enrichment.limit}
          remaining={enrichment.remaining}
          color={enrichment.color}
          limitReached={enrichment.limitReached}
          collapsed={collapsed}
        />
      )}
      {downloadsVisible && (
        <AgentUsageMeter
          label="Company downloads"
          infoText={DOWNLOADS_INFO}
          used={downloads.used}
          limit={downloads.limit}
          remaining={downloads.remaining}
          color={downloads.color}
          limitReached={downloads.limitReached}
          collapsed={collapsed}
        />
      )}
    </div>
  );
});

AgentUsagePanel.propTypes = {
  collapsed: PropTypes.bool
};

export default AgentUsagePanel;
